import React from 'react';
import type { Table, Order } from '../../types';
import { orderHasMeaningfulItems } from '../../lib/orderUtils';

interface TableMergeModalProps {
  selectedTables: Table[];
  orders: Order[];
  onConfirm: () => void;
  onCancel: () => void;
}

const TableMergeModal: React.FC<TableMergeModalProps> = ({
  selectedTables,
  orders,
  onConfirm,
  onCancel,
}) => {
  // Check if any table has active orders (numeric ids — Firestore may use string tableId)
  const hasActiveOrders = selectedTables.some(table => {
    const todayKey = new Date().toDateString();
    const tid = Number(table.id);
    return orders.some(order => {
      if (order.orderType !== 'dine-in' || Number(order.tableId) !== tid) return false;
      if (order.paymentStatus !== 'unpaid') return false;
      if (!orderHasMeaningfulItems(order)) return false;
      if (!(order.status === 'new' || order.status === 'preparing' || order.status === 'ready' || order.status === 'served')) return false;
      const orderDateKey = new Date(order.timestamp).toDateString();
      return orderDateKey === todayKey;
    });
  });

  const alreadyMerged = selectedTables.some(
    t => t.mergedWith && Array.isArray(t.mergedWith) && t.mergedWith.length > 1,
  );

  // Check if any table is reserved
  const hasReservedTables = selectedTables.some(table => table.status === 'reserved');

  // Check if all tables are available
  const allAvailable = selectedTables.every(table => table.status === 'available');

  // Calculate combined capacity
  const combinedCapacity = selectedTables.reduce((sum, table) => sum + table.capacity, 0);

  // Generate combined name (e.g., "Table 1+2")
  const combinedName = selectedTables.map(t => t.name).join('+');

  // Check if merge is allowed
  const canMerge =
    !hasActiveOrders &&
    !hasReservedTables &&
    allAvailable &&
    !alreadyMerged &&
    selectedTables.length >= 2;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <header className="p-4 border-b">
          <h2 className="text-xl font-bold text-gray-800">Merge Tables</h2>
        </header>
        <main className="p-4">
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-3">Selected tables to merge:</p>
            <div className="space-y-2 mb-4">
              {selectedTables.map(table => (
                <div
                  key={table.id}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded border"
                >
                  <div>
                    <span className="font-semibold">{table.name}</span>
                    <span className="text-sm text-gray-500 ml-2">Capacity: {table.capacity}</span>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${
                    table.status === 'available' ? 'bg-green-100 text-green-700' :
                    table.status === 'occupied' ? 'bg-red-100 text-red-700' :
                    table.status === 'reserved' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {table.status}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {!canMerge && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
              <p className="text-sm text-red-700 font-semibold mb-1">Cannot merge tables:</p>
              <ul className="text-xs text-red-600 list-disc list-inside space-y-1">
                {alreadyMerged && (
                  <li>One or more tables are already merged — split that group first</li>
                )}
                {hasActiveOrders && <li>One or more tables have active orders today</li>}
                {hasReservedTables && <li>One or more tables are reserved</li>}
                {!allAvailable && !hasReservedTables && !alreadyMerged && (
                  <li>All tables must be available</li>
                )}
                {selectedTables.length < 2 && <li>Select at least 2 tables to merge</li>}
              </ul>
            </div>
          )}

          {canMerge && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
              <p className="text-sm font-semibold text-blue-900 mb-2">Merged Table Details:</p>
              <div className="space-y-1 text-sm text-blue-800">
                <p><span className="font-semibold">Name:</span> {combinedName}</p>
                <p><span className="font-semibold">Combined Capacity:</span> {combinedCapacity}</p>
              </div>
            </div>
          )}
        </main>
        <footer className="p-4 border-t flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!canMerge}
            className={`px-4 py-2 text-white rounded transition-colors ${
              canMerge
                ? 'bg-blue-500 hover:bg-blue-600'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            Merge Tables
          </button>
        </footer>
      </div>
    </div>
  );
};

export default TableMergeModal;

